import type { Target } from "./types/Target.js";
import type { PackageInfos, DependencyMap } from "workspace-tools";
import { getPackageAndTask, getStartTargetId, getTargetId } from "./targetId.js";
import { builtInTargetTypes } from "./builtInTargetTypes.js";

/**
 * Checks whether a target represents a "phantom" target — one created for a package that
 * doesn't actually define the script for an npmScript target. Phantom targets should be excluded from `^` and `^^`
 * (topological/transitive) dependency expansion to avoid unwanted cross-package dependency chains.
 *
 * When phantom targets are later removed by `removeNodes` (because `shouldRun` returns false),
 * their same-package dependencies get reconnected to their cross-package dependents, creating
 * unnecessary work.
 *
 * Only npmScript target types can be considered phantom targets
 *
 * Returns true if the target should be EXCLUDED from dependency expansion.
 */
function isPhantomTarget(params: { targetId: string; task: string; targets: Map<string, Target>; packageInfos: PackageInfos }): boolean {
  const { targetId, task, targets, packageInfos } = params;

  const target = targets.get(targetId);

  // Only npmScript targets can be phantom — other types (worker, noop, etc.)
  // are real targets regardless of whether the package defines the script.
  if (!target?.packageName || target.type !== builtInTargetTypes.npmScript) {
    return false;
  }

  // If the package scripts don't include this task, it's a phantom target.
  const pkg = packageInfos[target.packageName];
  return !pkg?.scripts?.[task];
}

/**
 * Expands the dependency graph by adding all transitive dependencies of the given targets.
 */
export function expandDepSpecs(
  targets: Map<string, Target>,
  dependencyMap: DependencyMap,
  packageInfos: PackageInfos,
  enablePhantomTargetOptimization: boolean
): [string, string][] {
  const dependencies: [string, string][] = [];

  /**
   * Adds a dependency in the form of [from, to] to the dependency list.
   */
  const addDependency = (from: string, to: string) => {
    dependencies.push([from, to]);
  };

  /**
   * Finds all transitive dependencies, given a task and optionally a dependency list.
   */
  const findDependenciesByTask = (task: string, deps?: string[]) => {
    if (!deps) {
      return targetList.filter((needle) => needle.task === task).map((needle) => needle.id);
    }

    return targetList
      .filter((needle) => {
        const { task: needleTask, packageName: needlePackageName } = needle;
        return needleTask === task && deps.some((depPkg) => depPkg === needlePackageName);
      })
      .map((needle) => needle.id);
  };

  const targetList = [...targets.values()];

  for (const target of targetList) {
    const { depSpecs, packageName, id: to } = target;

    // Always start with a root node with a special "START_TARGET_ID"
    // because any node could potentially be part of the entry point in building the scoped target subgraph
    dependencies.push([getStartTargetId(), to]);

    // Skip any targets that have no "deps" specified
    if (!depSpecs || depSpecs.length === 0) {
      continue;
    }

    /**
     * Now for every deps defined, we need to "interpret" it based on the syntax:
     * - for any deps like package#task, we simply add the singular dependency (source could be a single package or all packages)
     * - for anything that starts with a "^", we add the package-tasks according to the topological package graph
     *    NOTE: in a non-strict mode (TODO), the dependencies can come from transitive task dependencies
     * - for anything that starts with a "^^", we add the package-tasks from the transitive dependencies in the topological
     *    package graph.
     * - for {"pkgA#task": ["dep"]}, we interpret to add "pkgA#dep"
     * - for anything that is a string without a "^", we treat that string as the name of a task, adding all targets that way
     *    NOTE: in a non-strict mode (TODO), the dependencies can come from transitive task dependencies
     *
     * We interpret anything outside of these conditions as invalid
     */
    for (const dependencyTargetId of depSpecs) {
      if (dependencyTargetId.includes("#")) {
        // id's with a # are package-task dependencies, or global
        // therefore, we must use getPackageAndTask() & getTargetId() to normalize the target id
        // (e.g. "build": ["build-tool#build"])
        const pkgAndTask = getPackageAndTask(dependencyTargetId);
        const normalizedDependencyTargetId = getTargetId(pkgAndTask.packageName, pkgAndTask.task);
        addDependency(normalizedDependencyTargetId, to);
      } else if (dependencyTargetId.startsWith("^^") && packageName) {
        // Transitive depdency (e.g. bundle: ['^^transpile'])
        const depTask = dependencyTargetId.substring(2);
        const targetDependencies = [...(getTransitiveGraphDependencies(packageName, dependencyMap) ?? [])];
        const dependencyTargetIds = findDependenciesByTask(depTask, targetDependencies);
        for (const from of dependencyTargetIds) {
          // Skip phantom targets: packages that don't define this task as a real npm script.
          if (!enablePhantomTargetOptimization || !isPhantomTarget({ targetId: from, task: depTask, targets, packageInfos })) {
            addDependency(from, to);
          }
        }
      } else if (dependencyTargetId.startsWith("^") && packageName) {
        // Topological dependency (e.g. build: ['^build'])
        const depTask = dependencyTargetId.substring(1);
        const targetDependencies = [...(dependencyMap.dependencies.get(packageName) ?? [])];
        const dependencyTargetIds = findDependenciesByTask(depTask, targetDependencies);
        for (const from of dependencyTargetIds) {
          // Skip phantom targets: packages that don't define this task as a real npm script.
          if (!enablePhantomTargetOptimization || !isPhantomTarget({ targetId: from, task: depTask, targets, packageInfos })) {
            addDependency(from, to);
          }
        }
      } else if (packageName) {
        // Add dependency on a specific package and given task name as dependency
        // (e.g. bundle: ['build'])
        const task = dependencyTargetId;
        if (targets.has(getTargetId(packageName, task))) {
          addDependency(getTargetId(packageName, task), to);
        }
      } else if (!dependencyTargetId.startsWith("^")) {
        // Global dependency - add all targets that match task name as dependency
        // (e.g. "#bundle": ['build'])
        const task = dependencyTargetId;
        const dependencyIds = findDependenciesByTask(task);
        for (const dependencyId of dependencyIds) {
          addDependency(dependencyId, to);
        }
      } else {
        throw new Error(`invalid pipeline config detected: ${target.id}, packageName: ${packageName}, dep: ${dependencyTargetId}`);
      }
    }
  }

  return dependencies;
}

/** Cached transitive task dependency */
const cachedTransitiveTaskDependencies: Map<string, "walk-in-progress" | Set<string>> = new Map();

/**
 * Gets a list of package names that are direct or indirect dependencies of rootPackageName in this.graph,
 * and caches them on the Pipeline.
 *
 * For example, this is useful for a bundling target that depends on all transitive dependencies to have been built.
 *
 * @param packageName the root package to begin walking from
 */
function getTransitiveGraphDependencies(packageName: string, dependencyMap: DependencyMap): Set<string> {
  const cachedResult = cachedTransitiveTaskDependencies.get(packageName);
  if (cachedResult) {
    return cachedResult === "walk-in-progress"
      ? // There is a recursive walk over this set of dependencies in progress.
        // If we hit this case, that means that a dependency of this package depends on it.
        //
        // In this case we return an empty set to omit this package and it's downstream from its
        // dependency
        new Set()
      : // we already computed this for this package, return the cached result.
        cachedResult;
  } else {
    // No cached result. Compute now with a recursive walk

    // mark that we are traversing this package to prevent infinite recursion
    // in cases of circular dependencies
    cachedTransitiveTaskDependencies.set(packageName, "walk-in-progress");

    const immediateDependencies = [...(dependencyMap.dependencies.get(packageName) ?? [])];

    // build the set of transitive dependencies by recursively walking the
    // immediate dependencies' dependencies.
    const transitiveDepSet = new Set<string>(immediateDependencies);
    for (const immediateDependency of immediateDependencies) {
      for (const transitiveSubDependency of getTransitiveGraphDependencies(immediateDependency, dependencyMap)) {
        transitiveDepSet.add(transitiveSubDependency);
      }
    }

    // Cache the result and return
    cachedTransitiveTaskDependencies.set(packageName, transitiveDepSet);
    return transitiveDepSet;
  }
}
